import styled, { keyframes } from "styled-components";

/* ── Animations ─────────────────────────────────────────── */
const fadeUp = keyframes`
  from { opacity: 0; transform: translateY(16px); }
  to   { opacity: 1; transform: translateY(0); }
`;

const shimmer = keyframes`
  0%   { background-position: -200% 0; }
  100% { background-position: 200% 0; }
`;

/* ── Page wrapper ───────────────────────────────────────── */
export const PageWrapper = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  min-height: 100vh;
  padding: 2rem 1rem;
  background: var(--bg-main);
  color: var(--text-main);
`;

/* ── Signed-in banner ───────────────────────────────────── */
export const AccountBanner = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  width: 100%;
  max-width: 640px;
  padding: 12px 20px;
  margin-bottom: 32px;
  border-radius: 12px;
  background: var(--surface-card, var(--bg-card, #fff));
  border: 1px solid var(--surface-border, rgba(0, 0, 0, 0.08));
  animation: ${fadeUp} 0.4s ease;
`;

export const AccountInfo = styled.div`
  display: flex;
  align-items: center;
  gap: 10px;
  font-size: clamp(0.95rem, 0.9rem + 0.3vw, 1.05rem);
  color: var(--text-color-secondary, #6b7280);

  strong {
    color: var(--text-color, var(--text-main));
    font-weight: 600;
  }
`;

export const SwitchButton = styled.button`
  display: inline-flex;
  align-items: center;
  gap: 6px;
  padding: 6px 14px;
  border: 1px solid var(--surface-border, rgba(0, 0, 0, 0.12));
  border-radius: 8px;
  background: transparent;
  color: var(--text-color-secondary, #6b7280);
  font-size: clamp(0.85rem, 0.8rem + 0.2vw, 0.95rem);
  font-weight: 500;
  cursor: pointer;
  transition: all 0.2s ease;

  &:hover {
    background: var(--surface-hover, rgba(0, 0, 0, 0.04));
    color: var(--text-color, var(--text-main));
    border-color: var(--text-color-secondary, #9ca3af);
  }
`;

/* ── Main content card ──────────────────────────────────── */
export const ContentCard = styled.div`
  width: 100%;
  max-width: 640px;
  border-radius: 20px;
  padding: 48px 40px;
  background: var(--surface-card, var(--bg-card, #fff));
  border: 1px solid var(--surface-border, rgba(0, 0, 0, 0.08));
  box-shadow: 0 4px 24px rgba(0, 0, 0, 0.06);
  animation: ${fadeUp} 0.5s ease;

  @media (max-width: 600px) {
    padding: 32px 20px;
  }
`;

export const Heading = styled.h1`
  font-size: clamp(1.6rem, 1.2rem + 1.2vw, 2.2rem);
  font-weight: 700;
  line-height: 1.35;
  margin: 0 0 8px;
  color: var(--text-color, var(--text-main));
`;

export const Subtitle = styled.p`
  font-size: clamp(1rem, 0.92rem + 0.5vw, 1.2rem);
  color: var(--text-color-secondary, #6b7280);
  margin: 0 0 36px;
  line-height: 1.5;
`;

/* ── Option cards (init-setup) ──────────────────────────── */
export const OptionsGrid = styled.div`
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 16px;

  @media (max-width: 500px) {
    grid-template-columns: 1fr;
  }
`;

export const OptionCard = styled.button`
  position: relative;
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 14px;
  padding: 32px 24px;
  border: 2px solid var(--surface-border, rgba(0, 0, 0, 0.08));
  border-radius: 16px;
  background: var(--surface-ground, var(--bg-main, #fafafa));
  cursor: pointer;
  transition: all 0.25s ease;
  color: var(--text-color, var(--text-main));

  &:hover {
    border-color: var(--primary-color, #6366f1);
    box-shadow: 0 0 0 3px rgba(99, 102, 241, 0.12);
    transform: translateY(-2px);
  }

  &:active {
    transform: translateY(0);
  }
`;

export const OptionIcon = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  width: 56px;
  height: 56px;
  border-radius: 14px;
  font-size: 1.5rem;
  background: ${({ $bg }) => $bg || "rgba(99, 102, 241, 0.1)"};
  color: ${({ $color }) => $color || "var(--primary-color, #6366f1)"};
`;

export const OptionTitle = styled.span`
  font-size: clamp(1.05rem, 1rem + 0.3vw, 1.2rem);
  font-weight: 600;
`;

export const OptionDescription = styled.span`
  font-size: clamp(0.95rem, 0.86rem + 0.3vw, 1.05rem);
  color: var(--text-color-secondary, #6b7280);
  line-height: 1.45;
`;

export const RecommendedBadge = styled.span`
  position: absolute;
  top: 12px;
  right: 12px;
  padding: 3px 10px;
  border-radius: 20px;
  font-size: clamp(0.75rem, 0.7rem + 0.2vw, 0.85rem);
  font-weight: 600;
  letter-spacing: 0.02em;
  background: linear-gradient(135deg, #6366f1, #a855f7);
  background-size: 200% 100%;
  animation: ${shimmer} 3s linear infinite;
  color: #fff;
`;

/* ── File upload area (ai-setup) ────────────────────────── */
export const UploadZone = styled.label`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: 12px;
  width: 100%;
  min-height: 180px;
  padding: 32px;
  border: 2px dashed ${({ $hasFile }) =>
    $hasFile
      ? "var(--primary-color, #6366f1)"
      : "var(--surface-border, rgba(0, 0, 0, 0.15))"};
  border-radius: 16px;
  background: ${({ $hasFile }) =>
    $hasFile
      ? "rgba(99, 102, 241, 0.05)"
      : "var(--surface-ground, var(--bg-main, #fafafa))"};
  cursor: pointer;
  transition: all 0.25s ease;
  color: var(--text-color-secondary, #6b7280);

  &:hover {
    border-color: var(--primary-color, #6366f1);
    background: rgba(99, 102, 241, 0.04);
  }

  input[type="file"] {
    display: none;
  }
`;

export const UploadIcon = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  width: 52px;
  height: 52px;
  border-radius: 50%;
  background: rgba(99, 102, 241, 0.1);
  color: var(--primary-color, #6366f1);
  font-size: 1.4rem;
`;

export const UploadText = styled.span`
  font-size: clamp(1rem, 0.92rem + 0.4vw, 1.15rem);
  text-align: center;
  line-height: 1.5;

  strong {
    color: var(--primary-color, #6366f1);
    font-weight: 600;
  }
`;

export const FileName = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 10px 16px;
  margin-top: 4px;
  border-radius: 10px;
  background: rgba(99, 102, 241, 0.08);
  color: var(--primary-color, #6366f1);
  font-size: clamp(0.95rem, 0.88rem + 0.3vw, 1.05rem);
  font-weight: 500;
`;

export const ExistingResumeCard = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  width: 100%;
  padding: 16px 20px;
  margin-bottom: 20px;
  border-radius: 12px;
  border: 1px solid var(--surface-border, rgba(0, 0, 0, 0.08));
  background: var(--surface-ground, var(--bg-main, #fafafa));

  .info {
    display: flex;
    align-items: center;
    gap: 12px;
    font-size: clamp(1rem, 0.92rem + 0.4vw, 1.15rem);
    color: var(--text-color, var(--text-main));
    font-weight: 500;
  }

  .info i {
    font-size: 1.2rem;
    color: var(--primary-color, #6366f1);
  }
`;

export const ButtonRow = styled.div`
  display: flex;
  align-items: center;
  gap: 12px;
  margin-top: 24px;
  width: 100%;

  & > * {
    flex: 1;
  }
`;

export const BackLink = styled.button`
  display: inline-flex;
  align-items: center;
  gap: 6px;
  margin-top: 20px;
  padding: 0;
  border: none;
  background: transparent;
  color: var(--text-color-secondary, #6b7280);
  font-size: clamp(0.95rem, 0.88rem + 0.3vw, 1.05rem);
  font-weight: 500;
  cursor: pointer;
  transition: color 0.2s ease;

  &:hover {
    color: var(--text-color, var(--text-main));
  }
`;
